import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Input, Select, Field, Stat, Textarea } from "@/components/UI";
import PriceUpdateModal from "@/components/PriceUpdateModal";
import { Plus, PencilSimple, Trash, Pill, ChartLine, Package as PackageIcon, ListChecks, Warning, Drop, TrendUp, CurrencyCircleDollar, ShoppingCart, Archive, ArrowCounterClockwise, UploadSimple, X, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { USAGE_UNITS, CATEGORIES, EMPTY, DOSE_UNITS_FOR, expiryBadge, EMPTY_BULL, EMPTY_PROTOCOL, muid } from "./constants";

export function SemenStock() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_BULL);
  const [editId, setEditId] = useState(null);
  const [batchFor, setBatchFor] = useState(null);
  const [priceModal, setPriceModal] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [view, setView] = useState("stock"); // "stock" | "purchases"
  const [purchases, setPurchases] = useState([]);

  const load = () => {
    api.get(`/semen-stock?include_archived=${includeArchived}`).then(r => setItems(r.data)).catch(() => {});
    api.get("/semen-stock/summary?months=6").then(r => setSummary(r.data)).catch(() => {});
    api.get("/semen-stock/purchases").then(r => setPurchases(r.data || [])).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [includeArchived]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bull_name.trim()) return toast.error("Boğa adı girin");
    try {
      if (editId) {
        // Düzenleme: yalnızca üstveri (fiyat burada güncellenmez — fiyat sadece parti/fatura ile)
        await api.patch(`/semen-stock/${editId}`, {
          bull_name: form.bull_name, bull_code: form.bull_code,
          payet_count: Number(form.payet_count || 0),
          min_stock: Number(form.min_stock || 0),
          semen_type: form.semen_type, supplier: form.supplier, notes: form.notes,
        });
        toast.success("Boğa bilgisi güncellendi");
      } else {
        await api.post("/semen-stock", {
          bull_name: form.bull_name, bull_code: form.bull_code,
          payet_count: Number(form.payet_count || 0),
          invoice_amount: Number(form.invoice_amount || 0),
          min_stock: Number(form.min_stock || 0),
          semen_type: form.semen_type, supplier: form.supplier, notes: form.notes,
        });
        toast.success("Yeni boğa sperma stoğuna eklendi");
      }
      setForm(EMPTY_BULL); setEditId(null); setShowForm(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };

  const startEdit = (b) => {
    setForm({
      bull_name: b.bull_name || "",
      bull_code: b.bull_code || "",
      payet_count: b.payet_count || 0,
      invoice_amount: 0,
      min_stock: b.min_stock ?? 5,
      semen_type: b.semen_type || "konvansiyonel",
      supplier: b.supplier || "",
      notes: b.notes || "",
    });
    setEditId(b.id);
    setShowForm(true);
  };

  const archive = async (b) => {
    try { await api.patch(`/semen-stock/${b.id}/archive`); toast.success(`"${b.bull_name}" arşive kaldırıldı`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };
  const unarchive = async (b) => {
    try { await api.patch(`/semen-stock/${b.id}/unarchive`); toast.success(`"${b.bull_name}" arşivden çıkarıldı`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };
  const del = async (b) => {
    if (!window.confirm(`"${b.bull_name}" boğası KALICI olarak silinsin mi? Bu işlem geri alınamaz. (Geçmiş tohumlama kayıtları korunur.)`)) return;
    try {
      await api.delete(`/semen-stock/${b.id}`);
      toast.success("Kalıcı olarak silindi");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };

  const submitBatch = async () => {
    if (!batchFor) return;
    const rc = Number(batchFor.received_count);
    if (!rc || rc <= 0) return toast.error("Gelen adet girin");
    try {
      const r = await api.post(`/semen-stock/${batchFor.bull.id}/batch`, {
        received_count: rc,
        invoice_amount: Number(batchFor.invoice_amount || 0),
        supplier: batchFor.supplier || null,
        note: batchFor.note || null,
      });
      const pricePart = r.data.price_changed ? ` · birim fiyat ${fmt(r.data.unit_price)} ₺` : "";
      toast.success(`Parti eklendi: yeni stok ${r.data.payet_count} payet${pricePart}`);
      setBatchFor(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };

  const fmt = (v) => Number(v || 0).toLocaleString("tr-TR");
  const canSeeCost = summary?.total_stock_value != null; // backend gizlemiş olabilir

  return (
    <div data-testid="semen-stock-tab">
      {/* KRİTİK STOK UYARISI — üstte belirgin */}
      {summary?.warning_flag && summary.low_stock_bulls?.length > 0 && (
        <Card className="mb-4 !bg-[#FBECEA] !border-[#E9B5AB]" data-testid="semen-warning-card">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[#F5D5CE] rounded-md">
              <ShoppingCart size={22} weight="duotone" className="text-[#8B2E1D]" />
            </div>
            <div className="flex-1">
              <h4 className="heading text-base text-[#8B2E1D] flex items-center gap-1.5">
                <Warning size={16} weight="fill" /> Sperma Siparişi Verilmeli
              </h4>
              <p className="text-xs text-[#8B2E1D] mt-0.5 mb-2">
                {summary.low_stock_bulls.length} boğa kritik seviyenin altında. Stoklar tükenmeden önce sipariş verin.
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid="semen-warning-bulls">
                {summary.low_stock_bulls.map(b => (
                  <span key={b.id}
                        className="inline-flex items-center gap-1 text-xs bg-white border border-[#E9B5AB] px-2 py-1 rounded-md">
                    <span className="font-semibold text-[#8B2E1D]">{b.bull_name}</span>
                    <span className="tabnum text-[#5e675e]">·</span>
                    <span className="tabnum text-[#5e675e]">{b.payet_count}/{b.min_stock} payet</span>
                    <span className="text-[10px] text-[#8a938c]">({b.semen_type})</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* KURUMSAL RAPOR PANELI — 5 KPI */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="semen-kpis">
        <Stat testId="semen-stat-payets" label="Toplam Payet" value={fmt(summary?.total_payet_count ?? 0)}
              unit={summary?.months_of_stock != null ? `≈ ${summary.months_of_stock} ay` : ""} tone="ok" />
        {canSeeCost && (
          <Stat testId="semen-stat-value" label="Toplam Stok Değeri" value={fmt(summary?.total_stock_value)} unit="₺" tone="accent" />
        )}
        <Stat testId="semen-stat-avg" label="Aylık Ort. Tüketim"
              value={fmt(summary?.monthly_avg_consumption ?? 0)} unit="payet" tone="accent" />
        <Stat testId="semen-stat-this-month" label="Bu Ay Tüketim"
              value={fmt(summary?.current_month_consumption ?? 0)} unit="payet" tone="warn" />
        {canSeeCost && (
          <Stat testId="semen-stat-this-month-expense" label="Bu Ay Gider"
                value={fmt(summary?.current_month_expense ?? 0)} unit="₺" tone="warn" />
        )}
      </div>

      {/* 6 AYLIK TREND GRAFİĞİ — TÜR DAĞILIMI (yığılmış) */}
      {summary?.series && summary.series.some(s => s.payets > 0) && (
        <Card className="mb-4" data-testid="semen-trend-chart">
          <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <TrendUp size={16} weight="duotone" className="text-[#2D4A36]" />
              <h4 className="heading text-base">Son 6 Ay — Aylık Payet Tüketimi (Tür Dağılımı)</h4>
            </div>
            {/* Dönem tür özeti */}
            <div className="flex flex-wrap gap-1.5 text-xs" data-testid="semen-type-totals">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#E4EEE6] text-[#2D4A36] font-medium"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#2D6A4F" }}></span>Dişi: <b className="tabnum">{fmt(summary.type_totals?.female ?? 0)}</b></span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#FBF1DE] text-[#8B6914] font-medium"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#DDA74F" }}></span>Erkek: <b className="tabnum">{fmt(summary.type_totals?.male ?? 0)}</b></span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#EEECE5] text-[#1A1D1A] font-medium"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#8a938c" }}></span>Konv.: <b className="tabnum">{fmt(summary.type_totals?.conv ?? 0)}</b></span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#2D4A36] text-white font-semibold" data-testid="semen-type-total-all">Toplam: <b className="tabnum">{fmt((summary.type_totals?.female ?? 0) + (summary.type_totals?.male ?? 0) + (summary.type_totals?.conv ?? 0))}</b></span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={summary.series} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                formatter={(v, key) => [`${v} payet`, ({ female: "Dişi", male: "Erkek", conv: "Konvansiyonel" }[key] || key)]}
                labelStyle={{ fontSize: 12 }}
                contentStyle={{ fontSize: 12 }} />
              <Legend formatter={(val) => ({ female: "Dişi", male: "Erkek", conv: "Konvansiyonel" }[val] || val)} wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="female" stackId="a" fill="#2D6A4F" name="female" />
              <Bar dataKey="male" stackId="a" fill="#DDA74F" name="male" />
              <Bar dataKey="conv" stackId="a" fill="#8a938c" name="conv" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* ALT SEKME: Sperma Stoğu / Geçmiş Alımlar */}
      <div className="flex gap-2 mb-4 max-w-md" data-testid="semen-subtabs">
        <button type="button" data-testid="semen-subtab-stock" onClick={() => setView("stock")}
          className={`flex-1 px-3 py-2 rounded-md text-sm font-semibold border transition-colors ${view === "stock" ? "bg-[#2D4A36] text-white border-[#2D4A36]" : "bg-white text-[#5e675e] border-[#E5E2DC] hover:border-[#2D4A36]"}`}>
          Sperma Stoğu
        </button>
        <button type="button" data-testid="semen-subtab-purchases" onClick={() => setView("purchases")}
          className={`flex-1 px-3 py-2 rounded-md text-sm font-semibold border transition-colors ${view === "purchases" ? "bg-[#2D4A36] text-white border-[#2D4A36]" : "bg-white text-[#5e675e] border-[#E5E2DC] hover:border-[#2D4A36]"}`}>
          Geçmiş Alımlar{purchases.length > 0 ? ` (${purchases.length})` : ""}
        </button>
      </div>

      {view === "stock" && (<>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <Button data-testid="semen-add-btn" onClick={() => { setEditId(null); setForm(EMPTY_BULL); setShowForm(v => !v); }}>
          <Plus size={14} className="inline mr-1" /> {showForm ? "Kapat" : "Yeni Boğa Ekle"}
        </Button>
        <button data-testid="semen-toggle-archived" onClick={() => setIncludeArchived(v => !v)}
                className="text-sm inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#E5E2DC] bg-white hover:bg-[#F5F2EC] text-[#5e675e]">
          <Archive size={15} weight="bold" /> {includeArchived ? "Aktifleri Göster" : "Arşivi Göster"}
        </button>
      </div>

      {showForm && (
        <Card className="mb-4">
          <h3 className="heading text-lg mb-3">{editId ? "Boğa Bilgisini Düzenle" : "Yeni Boğa Ekle"}</h3>
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-3">
            <Field label="Boğa Adı *">
              <Input required data-testid="semen-form-name" value={form.bull_name}
                     onChange={e => setForm({ ...form, bull_name: e.target.value })}
                     placeholder="Örn: Bruno, Ares" />
            </Field>
            <Field label="Boğa Kodu / Katalog No">
              <Input data-testid="semen-form-code" value={form.bull_code}
                     onChange={e => setForm({ ...form, bull_code: e.target.value })}
                     placeholder="Örn: HOL-2451" />
            </Field>
            <Field label={editId ? "Payet Sayısı *" : "Gelen Adet (Payet) *"}>
              <Input type="number" min="0" required data-testid="semen-form-payet" value={form.payet_count}
                     onChange={e => setForm({ ...form, payet_count: e.target.value })} />
            </Field>
            {!editId && (
              <Field label="Fatura Tutarı (₺)">
                <Input type="number" min="0" step="0.01" data-testid="semen-form-invoice" value={form.invoice_amount}
                       onChange={e => setForm({ ...form, invoice_amount: e.target.value })}
                       placeholder="Toplam fatura — birim fiyat otomatik hesaplanır" />
                <div className="text-[11px] text-[#5e675e] mt-1" data-testid="semen-form-computed-price">
                  Hesaplanan birim fiyat: <b className="tabnum">{(Number(form.payet_count) > 0 && Number(form.invoice_amount) > 0) ? fmt(Number(form.invoice_amount) / Number(form.payet_count)) : "0"} ₺/payet</b>
                </div>
              </Field>
            )}
            <Field label="Kritik Seviye (Payet)">
              <Input type="number" min="0" data-testid="semen-form-min-stock" value={form.min_stock}
                     onChange={e => setForm({ ...form, min_stock: e.target.value })}
                     placeholder="Varsayılan: 5" />
            </Field>
            <Field label="Tohum Tipi *">
              <Select data-testid="semen-form-type" value={form.semen_type}
                      onChange={e => setForm({ ...form, semen_type: e.target.value })}>
                <option value="konvansiyonel">Konvansiyonel</option>
                <option value="dişi">Dişi (Cinsiyet ayırt.)</option>
                <option value="erkek">Erkek (Cinsiyet ayırt.)</option>
              </Select>
            </Field>
            <Field label="Tedarikçi">
              <Input data-testid="semen-form-supplier" value={form.supplier}
                     onChange={e => setForm({ ...form, supplier: e.target.value })}
                     placeholder="Örn: Genetik AŞ" />
            </Field>
            <div className="md:col-span-2">
              <Field label="Notlar">
                <Textarea rows={2} value={form.notes || ""}
                          onChange={e => setForm({ ...form, notes: e.target.value })} />
              </Field>
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button type="submit" data-testid="semen-form-submit">
                <Plus size={14} className="inline mr-1" /> {editId ? "Güncelle" : "Ekle"}
              </Button>
              <Button type="button" onClick={() => { setShowForm(false); setEditId(null); setForm(EMPTY_BULL); }}
                      className="!bg-white !text-[#1A1D1A] border border-[#E5E2DC]">
                Vazgeç
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="!p-0">
        <div className="overflow-x-auto">
        <table className="data-table" data-testid="semen-table">
          <thead>
            <tr>
              <th>Boğa</th><th>Kod</th><th>Tip</th><th className="text-right">Payet</th>
              <th className="text-right">Kritik Seviye</th>
              <th className="text-right">₺/payet</th><th className="text-right">Değer</th>
              <th>Tedarikçi</th><th>Notlar</th>
              <th className="text-right whitespace-nowrap">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {items.map(x => (
              <tr key={x.id} data-testid={`semen-row-${x.id}`}
                  className={`${x.is_low_stock && !x.archived ? "bg-[#FBECEA]/40" : ""} ${x.archived ? "opacity-55" : ""}`}>
                <td className="font-semibold">
                  {x.bull_name}
                  {x.archived && (
                    <span data-testid={`semen-archived-badge-${x.id}`} className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-[#EEECE5] text-[#6b746c] px-1.5 py-0.5 rounded">
                      <Archive size={10} weight="bold" /> Arşivde
                    </span>
                  )}
                  {x.is_low_stock && !x.archived && (
                    <span data-testid={`semen-order-badge-${x.id}`}
                          className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#C45B45] text-white px-1.5 py-0.5 rounded animate-pulse">
                      <ShoppingCart size={10} weight="fill" /> Sipariş Et
                    </span>
                  )}
                </td>
                <td className="text-xs text-[#6b746c]">{x.bull_code || "—"}</td>
                <td>
                  <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          background: x.semen_type === "dişi" ? "#E4EEE6" : x.semen_type === "erkek" ? "#F7EBCC" : "#EEECE5",
                          color: x.semen_type === "dişi" ? "#2D4A36" : x.semen_type === "erkek" ? "#8B6914" : "#1A1D1A"
                        }}>
                    {x.semen_type}
                  </span>
                </td>
                <td className={`text-right tabnum font-bold ${x.is_low_stock && !x.archived ? "text-[#C45B45]" : ""}`}>
                  {fmt(x.payet_count)}
                  {x.is_low_stock && !x.archived && <Warning size={14} weight="fill" className="inline ml-1 text-[#C45B45]" />}
                </td>
                <td className="text-right tabnum text-xs text-[#6b746c]">
                  ≤ {fmt(x.min_stock ?? 5)}
                </td>
                <td className="text-right tabnum">{x.unit_price != null ? fmt(x.unit_price) : "—"}</td>
                <td className="text-right tabnum font-semibold">{x.total_value != null ? fmt(x.total_value) : "—"}</td>
                <td className="text-xs">{x.supplier || "—"}</td>
                <td className="text-xs text-[#6b746c]">{x.notes || "—"}</td>
                <td className="whitespace-nowrap text-right">
                  <div className="inline-flex items-center gap-1">
                    {!x.archived && (
                      <button data-testid={`semen-batch-${x.id}`} onClick={() => setBatchFor({ bull: x, received_count: "", invoice_amount: "", supplier: x.supplier || "", note: "" })}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-[#E4EEE6] text-[#2D4A36] hover:bg-[#d4e3d7] border border-[#c2d6c8]"
                              title="Yeni Parti (gelen adet + fatura → birim fiyat otomatik)">
                        <Plus size={14} weight="bold" />
                        <span className="hidden lg:inline">Yeni Parti</span>
                      </button>
                    )}
                    {!x.archived && (
                      <button data-testid={`semen-price-${x.id}`} onClick={() => setPriceModal(x)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-[#FEF6E0] text-[#8B6914] hover:bg-[#f7e6b8] border border-[#EED7A6]"
                              title="Fiyat Grafiği (otomatik hesaplanan birim fiyat)">
                        <CurrencyCircleDollar size={14} weight="bold" />
                        <span className="hidden lg:inline">₺ Grafiği</span>
                      </button>
                    )}
                    {!x.archived && (
                      <button data-testid={`semen-edit-${x.id}`} onClick={() => startEdit(x)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-[#F5F2EC] text-[#1A1D1A] hover:bg-[#eae5db] border border-[#E5E2DC]"
                              title="Düzenle">
                        <PencilSimple size={14} weight="bold" />
                        <span className="hidden lg:inline">Düzenle</span>
                      </button>
                    )}
                    {x.archived ? (
                      <button data-testid={`semen-unarchive-${x.id}`} onClick={() => unarchive(x)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-[#E4EEE6] text-[#2D4A36] hover:bg-[#d4e3d7] border border-[#c2d6c8]"
                              title="Arşivden Geri Getir">
                        <ArrowCounterClockwise size={14} weight="bold" />
                        <span className="hidden lg:inline">Geri Getir</span>
                      </button>
                    ) : (
                      <button data-testid={`semen-archive-${x.id}`} onClick={() => archive(x)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-[#F5F2EC] text-[#6b746c] hover:bg-[#eae5db] border border-[#E5E2DC]"
                              title="Arşive Kaldır (geçmiş tohumlamalar korunur)">
                        <Archive size={14} weight="bold" />
                        <span className="hidden lg:inline">Arşivle</span>
                      </button>
                    )}
                    {isOwner && (
                      <button data-testid={`semen-del-${x.id}`} onClick={() => del(x)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-[#FBECEA] text-[#C45B45] hover:bg-[#f5d5ce] border border-[#f2d4cb]"
                              title="Kalıcı Sil (yalnızca Admin — geri alınamaz)">
                        <Trash size={14} weight="bold" />
                        <span className="hidden lg:inline">Kalıcı Sil</span>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={10} className="text-center py-8 text-[#8a938c]">{includeArchived ? "Arşivde boğa yok" : "Sperma stoğunda kayıt yok"}</td></tr>}
          </tbody>
        </table>
        </div>
      </Card>
      </>)}

      {view === "purchases" && (
        <Card className="!p-0" data-testid="semen-purchases-card">
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="semen-purchases-table">
              <thead>
                <tr>
                  <th>Parti Tarihi</th><th>Boğa</th>
                  <th className="text-right">Gelen Adet</th>
                  <th className="text-right">Birim Fiyat</th>
                  <th className="text-right">Tutar</th>
                  <th>Tedarikçi</th><th>Fatura / Not</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map(p => (
                  <tr key={p.id} data-testid={`semen-purchase-row-${p.id}`}>
                    <td className="tabnum whitespace-nowrap">{p.date}</td>
                    <td className="font-semibold">{p.bull_name}</td>
                    <td className="text-right tabnum font-bold text-[#2D4A36]">+{fmt(p.received_count)}</td>
                    <td className="text-right tabnum">{p.unit_price != null ? `${fmt(p.unit_price)} ₺` : "—"}</td>
                    <td className="text-right tabnum font-semibold">{p.cost != null ? `${fmt(p.cost)} ₺` : "—"}</td>
                    <td className="text-xs">{p.supplier || "—"}</td>
                    <td className="text-xs text-[#6b746c]">{p.notes || "—"}</td>
                  </tr>
                ))}
                {!purchases.length && <tr><td colSpan={7} className="text-center py-8 text-[#8a938c]">Henüz parti alımı kaydı yok. "Sperma Stoğu" sekmesinde bir boğaya "Yeni Parti" ekleyin.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Yeni Parti Modalı — Gelen Adet + Fatura → birim fiyat otomatik */}
      {batchFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setBatchFor(null)}>
          <Card className="max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="heading text-lg mb-1">Yeni Parti — {batchFor.bull.bull_name}</h3>
            <div className="text-xs text-[#5e675e] mb-3">
              Şu anki stok: <b className="tabnum">{fmt(batchFor.bull.payet_count)}</b> payet
              {batchFor.bull.unit_price != null && <> · mevcut birim fiyat <b className="tabnum">{fmt(batchFor.bull.unit_price)} ₺</b></>}
            </div>
            <Field label="Gelen Adet (payet) *">
              <Input type="number" min="1" data-testid="semen-batch-count" placeholder="Örn: 50"
                     value={batchFor.received_count}
                     onChange={e => setBatchFor({ ...batchFor, received_count: e.target.value })} />
            </Field>
            <Field label="Fatura Tutarı (₺)">
              <Input type="number" min="0" step="0.01" data-testid="semen-batch-invoice"
                     placeholder="Toplam fatura — birim fiyat otomatik hesaplanır"
                     value={batchFor.invoice_amount}
                     onChange={e => setBatchFor({ ...batchFor, invoice_amount: e.target.value })} />
            </Field>
            <Field label="Tedarikçi">
              <Input data-testid="semen-batch-supplier" value={batchFor.supplier}
                     onChange={e => setBatchFor({ ...batchFor, supplier: e.target.value })}
                     placeholder="Örn: Genetik AŞ" />
            </Field>
            <Field label="Not (opsiyonel)">
              <Input data-testid="semen-batch-note" value={batchFor.note}
                     onChange={e => setBatchFor({ ...batchFor, note: e.target.value })}
                     placeholder="Örn: Fatura no, tedarikçi" />
            </Field>
            <div className="text-xs my-2 space-y-0.5" data-testid="semen-batch-preview">
              <div className="text-[#3E5B36] tabnum">Yeni stok: <b>{fmt(Number(batchFor.bull.payet_count) + Number(batchFor.received_count || 0))}</b> payet</div>
              {Number(batchFor.received_count) > 0 && Number(batchFor.invoice_amount) > 0 && (
                <div className="text-[#8B6914] tabnum">Hesaplanan birim fiyat: <b>{fmt(Number(batchFor.invoice_amount) / Number(batchFor.received_count))} ₺/payet</b></div>
              )}
            </div>
            <div className="flex gap-2">
              <Button data-testid="semen-batch-submit" onClick={submitBatch}>Partiyi Ekle</Button>
              <Button onClick={() => setBatchFor(null)} className="!bg-white !text-[#1A1D1A] border border-[#E5E2DC]">Vazgeç</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Fiyat Grafiği (salt-okunur) — birim fiyat yalnızca parti/fatura ile değişir */}
      {priceModal && (
        <PriceUpdateModal
          item={{ id: priceModal.id, name: priceModal.bull_name, unit_price: priceModal.unit_price }}
          historyEndpoint={`/semen-stock/${priceModal.id}/price-history`}
          trendEndpoint={`/semen-stock/${priceModal.id}/price-trend?months=12`}
          unitLabel="payet"
          readOnly
          title="Sperma Fiyat Grafiği"
          subtitle={`${priceModal.bull_name}${priceModal.bull_code ? ` · ${priceModal.bull_code}` : ""} · birim fiyat = fatura ÷ gelen adet`}
          onDone={() => { setPriceModal(null); load(); }}
          onClose={() => setPriceModal(null)}
        />
      )}
    </div>
  );
}



// ============================================================
// HIZLI ALIM MODALI — ilaç mal girişi (miktar + fiyat) → stok + finans
// ============================================================
